import { useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

// Clamp long user-authored text with a Show more toggle. The toggle renders
// only when the text actually overflows, so short values pay no UI cost.
// Collapsed on every mount by design — this is a summary view, not a reader.
export function ClampText({ text, lines = 3, className }: { text: string; lines?: 2 | 3; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    measure();
    // Wrapping changes with container width — collapsing the graph peek
    // resizes the workspace column — so re-measure on element resize, not
    // just when the text changes.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, lines]);

  return (
    <div className={className}>
      <div
        ref={ref}
        className={cn('max-w-prose whitespace-pre-wrap', !expanded && (lines === 2 ? 'line-clamp-2' : 'line-clamp-3'))}
      >
        {text}
      </div>
      {(overflows || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-0.5 text-xs text-muted-foreground underline-offset-2 hover:underline"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}
